import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getAmountToPay, getAmountToReceive } from '@/features/exchange/lib';
import { useAssetsExchangeRates } from '../api/queries';
import { NUMERIC_CONFIG } from '@/shared/config';
import { applyAmountPrecision, formatReceivedPrice } from '@/shared/lib';
import Big from 'big.js';

type UseExchangeConversionProps = {
  amountToPay: string;
  amountToReceive: string;
  assetCodeToPay: string;
  assetCodeToReceive: string;
  setExchangeRate: (rate: { leftPart: string; rightPart: string }) => void;
  setAmountToPay: (amount: string) => void;
  setAmountToReceive: (amount: string) => void;
};

const useExchangeConversion = ({
  amountToPay,
  amountToReceive,
  assetCodeToPay,
  assetCodeToReceive,
  setExchangeRate,
  setAmountToPay,
  setAmountToReceive,
}: UseExchangeConversionProps) => {
  const { t } = useTranslation();
  const {
    data: assetsExchangeRatesData,
    isLoading,
    isFetching,
  } = useAssetsExchangeRates(assetCodeToPay, assetCodeToReceive);
  const amountToPayRef = useRef(amountToPay);
  const amountToReceiveRef = useRef(amountToReceive);
  const pairKeyRef = useRef<string>('');

  useEffect(() => {
    amountToPayRef.current = amountToPay;
  }, [amountToPay]);

  useEffect(() => {
    amountToReceiveRef.current = amountToReceive;
  }, [amountToReceive]);

  const recalculateAmountToReceive = useCallback(
    (amountToPay: string) => {
      if (!assetsExchangeRatesData || !assetCodeToPay || !assetCodeToReceive)
        return;
      const { symbol, priceBid, priceAsk, assetLeft, assetRight } =
        assetsExchangeRatesData;

      const assetToReceive =
        assetLeft.code === assetCodeToReceive ? assetLeft : assetRight;

      const amountToReceive = getAmountToReceive(
        symbol,
        assetCodeToPay,
        assetCodeToReceive,
        priceBid,
        priceAsk,
        amountToPay
      );

      const formatted = applyAmountPrecision(
        amountToReceive,
        assetToReceive.precision ?? NUMERIC_CONFIG.precision.default,
        'credit'
      );

      setAmountToReceive(new Big(formatted).toFixed());
    },
    [assetsExchangeRatesData, assetCodeToPay, assetCodeToReceive]
  );

  const recalculateAmountToPay = useCallback(
    (amountToReceive: string) => {
      if (!assetsExchangeRatesData || !assetCodeToPay || !assetCodeToReceive)
        return;
      const { symbol, priceBid, priceAsk, assetLeft, assetRight } =
        assetsExchangeRatesData;

      const assetToPay =
        assetLeft.code === assetCodeToPay ? assetLeft : assetRight;

      const amountToPay = getAmountToPay(
        symbol,
        assetCodeToPay,
        assetCodeToReceive,
        priceBid,
        priceAsk,
        amountToReceive
      );

      const formatted = applyAmountPrecision(
        amountToPay,
        assetToPay.precision ?? NUMERIC_CONFIG.precision.default,
        'credit'
      );

      setAmountToPay(new Big(formatted).toFixed());
    },
    [assetsExchangeRatesData, assetCodeToPay, assetCodeToReceive]
  );

  const exchangeRateDescription = useMemo(() => {
    if (!assetsExchangeRatesData || !assetCodeToPay || !assetCodeToReceive)
      return '';
    const { symbol, priceBid, priceAsk, displayRateAsk, displayRateBid } =
      assetsExchangeRatesData;

    let leftAsset = '';
    let rightAsset = '';
    let displayRate = 0;

    if (symbol === `${assetCodeToPay}${assetCodeToReceive}`) {
      leftAsset = assetCodeToPay;
      rightAsset = assetCodeToReceive;
      displayRate = displayRateBid ?? priceBid;
    } else if (symbol === `${assetCodeToReceive}${assetCodeToPay}`) {
      leftAsset = assetCodeToReceive;
      rightAsset = assetCodeToPay;
      displayRate = displayRateAsk ?? priceAsk;
    }

    const exchangeRateRoundedString = formatReceivedPrice(displayRate);

    const leftPart = `1 ${leftAsset}`;
    const rightPart = `${exchangeRateRoundedString} ${rightAsset}`;
    const description = `${t('exchange.exchangeRate')}: ${leftPart} ≈ ${rightPart}`;

    setExchangeRate({ leftPart, rightPart });

    return description;
  }, [
    assetsExchangeRatesData,
    assetCodeToPay,
    assetCodeToReceive,
    setExchangeRate,
    t,
  ]);

  useEffect(() => {
    const nextPairKey = `${assetCodeToPay}|${assetCodeToReceive}`;
    const pairChanged = nextPairKey !== pairKeyRef.current;

    // Only auto-recalculate when the asset pair changes (not on amount changes),
    // and only after the rates request is resolved.
    if (!pairChanged) return;
    if (!assetsExchangeRatesData) return;
    if (isFetching || isLoading) return;

    pairKeyRef.current = nextPairKey;

    const isOnlyAmountToReceiveEntered =
      !(Number(amountToPayRef.current) > 0) &&
      Number(amountToReceiveRef.current) > 0;

    if (isOnlyAmountToReceiveEntered) {
      recalculateAmountToPay(String(amountToReceiveRef.current));
      return;
    }

    recalculateAmountToReceive(String(amountToPayRef.current));
  }, [
    assetCodeToPay,
    assetCodeToReceive,
    assetsExchangeRatesData,
    isFetching,
    isLoading,
    recalculateAmountToPay,
    recalculateAmountToReceive,
  ]);

  return {
    exchangeRateDescription,
    isConversionLoading: isLoading,
    recalculateAmountToReceive,
    recalculateAmountToPay,
  };
};

export { useExchangeConversion };
